/**
 * Audit Sessions List Page
 *
 * Displays all audit sessions with their status, progress, and management options.
 * Redesigned to use the new comprehensive AuditSessionsFilters component.
 */

import type { Community } from '@communities/community/domain/Community';
import { useCurrentCommunityId } from '@communities/community/hooks';
import { HeroBackground } from '@core/components/HeroBackground';
import { sortAuditSessions, applyFilters } from '@internal-audit/components/AuditSessionsFilters/helpers';
import type { AuditSessionFilters, AuditSessionSort, FilterOptions, AuditSessionPageSize } from '@internal-audit/components/AuditSessionsFilters/types';
import { createEmptyFilters, createDefaultSort } from '@internal-audit/components/AuditSessionsFilters/types';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useAuditSessionFilterOptions } from '@internal-audit/hooks/useAuditSessionFilterOptions';
import { useGetAuditSessions, type PaginationOptions } from '@internal-audit/hooks/useGetAuditSessions';
import { useGetAuditSessionsForHeader } from '@internal-audit/hooks/useGetAuditSessionsForHeader';
import { routes } from '@internal-audit/routes';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { SessionsPageContent } from './components/SessionsPageContent';
import { SessionsErrorDisplay, CommunityNotFound } from './components/SessionsPageErrorHandler';
import { SessionsPageHeader } from './components/SessionsPageHeader';

// eslint-disable-next-line max-statements
export function AuditSessionsListPage() {
  // Scroll to top when page loads for accessibility and standard web practice
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const communityId = useCurrentCommunityId();

  // Ref for scrolling to content section instead of page top
  const contentSectionRef = useRef<HTMLDivElement>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<AuditSessionPageSize>(50); // Default to 50 for good balance between performance and data visibility

  const paginationOptions: PaginationOptions = {
    page: currentPage,
    pageSize,
  };

  // Separate hooks for header and table data
  const { sessions, loading: sessionsLoading, error: sessionsError, refetch } = useGetAuditSessions(communityId, paginationOptions);
  const { headerSessions, community, headerLoading, headerError } = useGetAuditSessionsForHeader(communityId);
  const { filterOptions: hookFilterOptions, loading: filterOptionsLoading } = useAuditSessionFilterOptions(communityId || '');

  // New state management for comprehensive filtering and sorting
  const [filters, setFilters] = useState<AuditSessionFilters>(createEmptyFilters());
  const [sort, setSort] = useState<AuditSessionSort>(createDefaultSort());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Transform hook filter options to match our FilterOptions interface
  const filterOptions: FilterOptions = useMemo(() => {
    if (!hookFilterOptions) {
      return {
        availableLabels: [],
        availableTopics: [],
        availableLanguages: [],
      };
    }

    return {
      availableLabels: hookFilterOptions.standardLabels.map(label => label.labelName).sort(),
      availableTopics: hookFilterOptions.topics.slice().sort(),
      availableLanguages: hookFilterOptions.languages.slice().sort(),
    };
  }, [hookFilterOptions]);

  // Apply filters and sorting to sessions using helper functions
  const filteredAndSortedSessions = useMemo(() => {
    const filtered = applyFilters(sessions, filters);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return sortAuditSessions(filtered as unknown as any, sort) as unknown as FlexibleAuditSession[];
  }, [sessions, filters, sort]);

  // Show error dialog when there's an error
  useEffect(() => {
    if (sessionsError) {
      setShowErrorDialog(true);
    }
  }, [sessionsError]);

  const handleCreateSession = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleCloseErrorDialog = useCallback(() => {
    setShowErrorDialog(false);
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
  }, []);

  const handleViewSession = useCallback(
    (session: FlexibleAuditSession) => {
      if (!communityId) return;
      navigate(routes.internalAudit.community.auditDetail(communityId, session.id));
    },
    [navigate, communityId]
  );

  // Pagination handlers
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: AuditSessionPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size

    // Scroll to content section (filters/table area) instead of page top
    setTimeout(() => {
      contentSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100); // Small delay to ensure content is rendered
  }, []);

  // Calculate pagination state for server-side pagination
  // Since we don't know the exact total count, we use a simple approach:
  // - If we get a full page (100 items), assume there might be more pages
  // - Show current page + 1 if we have a full page, otherwise just current page
  const totalPages = sessions.length === pageSize ? currentPage + 1 : currentPage;

  // Update refetch calls to use pagination
  const handleRefetch = useCallback(() => {
    refetch({ page: currentPage, pageSize });
  }, [refetch, currentPage, pageSize]);

  const handleSessionCreatedWithPagination = useCallback(() => {
    // When a new session is created, go to first page to see it
    setCurrentPage(1);
    refetch({ page: 1, pageSize });
  }, [refetch, pageSize]);

  const handleDeleteSuccessWithPagination = useCallback(async () => {
    // After delete, stay on current page but refetch
    await refetch({ page: currentPage, pageSize });
  }, [refetch, currentPage, pageSize]);

  // Don't render main content if there's an error - ErrorDisplay will handle it
  if (sessionsError || headerError) {
    return (
      <SessionsErrorDisplay
        sessionsError={sessionsError || headerError}
        showErrorDialog={showErrorDialog}
        onCloseErrorDialog={handleCloseErrorDialog}
        onRefetch={handleRefetch}
      />
    );
  }

  // Show error state if community data is missing (shouldn't happen with optimized query)
  if (!community && !headerLoading) {
    return <CommunityNotFound onRefetch={handleRefetch} />;
  }

  return (
    <div>
      <HeroBackground imageUrl={(community as Community)?.superHeroArtUrl} />
      <SessionsPageHeader community={community} sessions={headerSessions} sessionsLoading={headerLoading} />

      <div ref={contentSectionRef}>
        <SessionsPageContent
          community={community}
          communityId={communityId || ''}
          currentPage={currentPage}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          filteredAndSortedSessions={filteredAndSortedSessions}
          filters={filters}
          isCreateDialogOpen={isCreateDialogOpen}
          pageSize={pageSize}
          sessionsLoading={sessionsLoading}
          sort={sort}
          totalPages={totalPages}
          onCloseCreateDialog={handleCloseCreateDialog}
          onCreateSession={handleCreateSession}
          onDeleteSuccess={handleDeleteSuccessWithPagination}
          onFiltersChange={setFilters}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSessionCreated={handleSessionCreatedWithPagination}
          onSortChange={setSort}
          onViewSession={handleViewSession}
        />
      </div>
    </div>
  );
}
